import React, { useState, useCallback } from 'react';
import styled from 'styled-components/macro';
import useRequest from 'src/api/useRequest';
import useService from 'src/hooks/useService';
import { useLocation, useNavigate } from 'react-router-dom';
import QueryString from 'qs';
import { tmarkRightsServiceProvider } from 'src/api/service/tmarkRightsService';

import SearchResult from './SearchResult';
import ResultLoading from 'src/components/ui-kit/ResultLoading';
import TmarkClassTag from 'src/components/layout/tag/TmarkClassTag';
import { tryParseInt } from 'src/utils/formatters';

import { FormType, globalComponents } from 'src/types/globalTypes';

import { getCookie, setCookie } from 'typescript-cookie'
import { useEffect, useRef } from 'react';
import {
  IReqTmarkRightsPage,
  IResTmarkRightsPage,
  TmarkOrderOptions,
} from 'src/types/api/trademarkRightsApiTypes';
import {
  Table,
  Th,
  Td,
  ListWrapper,
  ListRow,
  TypeItem,
  ListContent,
  ListItem,
  ListTitle,
  ListItemContent,
} from 'src/components/contents/search/result/SearchResult';
import { rowComponents } from 'src/utils/indexs';
import { useStore } from 'src/hooks/useStore';
import TmarkResultMobile from './TmarkResultMobile';
import TmarkSearchTip from './TmarkSearchTip';

export const Thead = styled.div<{ bgcolor: string }>`
  display: grid;
  grid-template-columns: 1fr 1fr 1fr 2fr 2fr 1fr;
  background-color: ${(prop) => prop.bgcolor};
  border-bottom: 1px solid ${(prop) => prop.bgcolor};
  align-items: center;
  justify-items: center;
  border-radius: 8px 8px 0px 0px;
`;
const Tr = styled.div<{ bgcolor: string }>`
  display: grid;
  grid-template-columns: 1fr 1fr 1fr 2fr 2fr 1fr;
  width: 100%;
  border-bottom: 1px solid ${(prop) => prop.bgcolor};
  cursor: pointer;
  :hover {
    background-color: ${(prop) => prop.bgcolor};
  }
`;

const Img = styled.img`
  height: 72px;
  box-shadow: 5px 5px 11px rgba(67, 67, 67, 0.2);
`;

const tmarkRightsService = tmarkRightsServiceProvider();

/**
 * 商標搜尋結果頁面
 */
const TmarkResult: React.FC = () => {
  const path = useService();
  const navigate = useNavigate();
  const isMobile = useStore((state) => state.isMobile);
  const { search } = useLocation();
  const params = QueryString.parse(search, { ignoreQueryPrefix: true });

  // 表格常數
  const SEPRATOR = '，'; //名稱間隔
  const component = globalComponents.T;
  const linkColor = component.color.seventh;

  const [formType, setFormType] = useState<FormType>(getCookie('formType') == null || getCookie('formType') === FormType.list ? FormType.list : FormType.table);
  const [pageNo, setPageNo] = useState<number>(tryParseInt(params.pageNo));
  const [pageSize, setPageSize] = useState<number>(getCookie('pageSize') == null ? tryParseInt(params.pageSize) : getCookie('pageSize'));
  const [orderCondition, setOrderCondition] = useState<string | number>(
    params.orderBy?.toString() || TmarkOrderOptions[0].value,
  );

  // 將搜尋條件放入state
  const [reqParams, setReqParams] = useState<IReqTmarkRightsPage>({
    ...params,
    pageNo: typeof tryParseInt(params.pageNo) === 'number' ? tryParseInt(params.pageNo) : 1,
  });

  // 搜尋條件
  const tip = <TmarkSearchTip reqParams={reqParams} />;

  /** 跳轉至案件明細頁面 */
  const handleOnClick = (examNo: string, tmarkClass: string) => {
    navigate(process.env.PUBLIC_URL + '/trademarkRights/detail', {
      state: {
        examNo: examNo,
        tmarkClass: tmarkClass,
      },
    });
  };
  
  const handleKeyDown = (
    e: React.KeyboardEvent<HTMLElement>, 
    callback: () => void
  ): void => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      callback();
    }
  };

  const isFirstRender = useRef(true);

  useEffect(() => {
    if (!isFirstRender.current) {
      setReqParams(prevParams => ({
        ...prevParams,
        pageNo,
        pageSize,
        orderBy: orderCondition
      }));
    }
  }, [pageNo, pageSize, orderCondition, setReqParams]);

  const [isLoading, , res] = useRequest<IResTmarkRightsPage>(
    useCallback(() => {
      // 執行原本的函數
      return tmarkRightsService.getTmarkRights(reqParams);
    }, [reqParams]),
  );

  useEffect(() => {
    isFirstRender.current = false;
  }, []);

  const tableForm = res?.data && (
    <Table>
      <Thead bgcolor={component.color.third}>
        <Th>商標種類</Th>
        <Th>註冊號數</Th>
        <Th>商標圖樣</Th>
        <Th>商標名稱</Th>
        <Th>商標權人</Th>
        <Th>權利期間</Th>
      </Thead>
      <tbody>
        {res.data.map((item, index) => {
          index += 1;
          return (
            <Tr
              key={index}
              bgcolor={component.color.third}
              onClick={() => handleOnClick(item.examNo, item.tmarkClass)}
			  tabIndex={0}
			  role="button"
			  onKeyDown={(e) => handleKeyDown(e, () => handleOnClick(item.examNo, item.tmarkClass))}
            >
              <Td>
                <TmarkClassTag type={item.tmarkClass} />
              </Td>
              <Td>{item.examNo}</Td>
              <Td>
                <Img src={item.tmarkImageUrl} alt="" />
              </Td>
              <Td style={{ color: linkColor }}>{item.tmarkName}</Td>
              <Td>{rowComponents(item.holdersNameC, SEPRATOR)}</Td>
              <Td>
                {item.regDate}~{item.deadlineDate}
              </Td>
            </Tr>
          );
        })}
      </tbody>
    </Table>
  );

  const listForm = res?.data && (
    <ListWrapper>
      {res.data.map((item, index) => {
        index += 1;
        return (
          <ListRow
            key={index}
            bgcolor={component.color.third}
            onClick={() => handleOnClick(item.examNo, item.tmarkClass)}
			tabIndex={0}
			role="button"
			onKeyDown={(e) => handleKeyDown(e, () => handleOnClick(item.examNo, item.tmarkClass))}
          >
            <TypeItem>
              <Img src={item.tmarkImageUrl} alt="" />
            </TypeItem>
            <ListContent>
              <ListItem>
                <TmarkClassTag type={item.tmarkClass} />
              </ListItem>
              <ListItem>
                <ListTitle>註冊號數：</ListTitle>
                <ListItemContent>{item.examNo}</ListItemContent>
              </ListItem>
              <ListItem>
                <ListTitle>商標權人：</ListTitle>
                <ListItemContent>{rowComponents(item.holdersNameC, SEPRATOR)}</ListItemContent>
              </ListItem>
              <ListItem>
                <ListTitle>商標名稱：</ListTitle>
                <ListItemContent style={{ color: linkColor }}>{item.tmarkName}</ListItemContent>
              </ListItem>
              <ListItem>
                <ListTitle>權利期間：</ListTitle>
                <ListItemContent>
                  {item.regDate}~{item.deadlineDate}
                </ListItemContent>
              </ListItem>
            </ListContent>
          </ListRow>
        );
      })}
    </ListWrapper>
  );
  const mobileForm = res?.data && (
    <TmarkResultMobile data={res.data} colorType={component.color} handleOnClick={handleOnClick} />
  );

  const desktopForm = formType === FormType.list ? listForm : tableForm;
  setCookie('formType', formType, { secure: true });
  const showForm = isMobile ? mobileForm : desktopForm;

  return (
    <SearchResult
      title={component.title}
      bannerBgImg={component.bgImgResultBanner}
      image={component.hoverImage}
      imgDot={component.bgImgDot}
      bgColor={component.color}
      returnPageUrl={path}
      tip={tip}
      formType={formType}
      setFormType={setFormType}
      page={res?.page}
      pageNo={pageNo}
      setPageNo={setPageNo}
      pageSize={pageSize}
      setPageSize={setPageSize}
      orderOptions={TmarkOrderOptions}
      orderCondition={orderCondition}
      setOrderCondition={setOrderCondition}
      reqParams={reqParams}
    >
      {isLoading ? <ResultLoading color={component.color.second} /> : showForm}
    </SearchResult>
  );
};

export default TmarkResult;
